import * as Accordion from '@radix-ui/react-accordion';
import { motion } from 'framer-motion';

import { OpenItem } from '@/components/modules/app-navigation/open/item';
import { OpenSavedItems } from '@/components/modules/app-navigation/open/saved-items';
import { APP_NAV_ITEM_AI, APP_NAV_ITEM_HOME, APP_NAV_ITEM_NOTIFICATIONS, AppNavItem } from '@/lib/constants';
import { SavedItemModel } from '@/lib/models/saved-item/types';
import { tv } from '@/utils/styles';

import { NavButton } from '../nav-button';
import { openNavVariants } from '../utils/transitions';

interface Props {
  isOpen: boolean | undefined;
  openAccordionItems: string[] | undefined;
  handleOpenAccordionItems: (newOpenItems: string[]) => void;
  handleOpenNotifications: () => void;
  handleNavigateTo: (url?: string) => void;
  navItems: AppNavItem[];
  isUserNotificationsEnabled?: boolean;
  isAssistantEnabled?: boolean;
  savedItems?: SavedItemModel[];
  onDeleteSavedItem?: (savedItem: SavedItemModel) => void;
}

const AppNavigationOpen = ({
  isOpen,
  openAccordionItems,
  handleOpenAccordionItems,
  navItems,
  handleOpenNotifications,
  handleNavigateTo,
  isUserNotificationsEnabled = false,
  isAssistantEnabled = false,
  savedItems = [],
  onDeleteSavedItem,
}: Props) => {
  const visibility = isOpen ? 'visible' : 'hidden';

  const { base, sectionHeader } = openNavStyles({ isOpen });

  return (
    <motion.nav variants={openNavVariants} animate={visibility} className={base()}>
      <NavButton
        onClick={() => handleNavigateTo(APP_NAV_ITEM_HOME.url)}
        item={APP_NAV_ITEM_HOME}
        isOpen
        visibility={visibility}
      />
      {isUserNotificationsEnabled ? (
        <NavButton
          onClick={handleOpenNotifications}
          item={APP_NAV_ITEM_NOTIFICATIONS}
          isOpen
          visibility={visibility}
          enableBadge
        />
      ) : null}
      {isAssistantEnabled && (
        <NavButton
          onClick={() => handleNavigateTo(APP_NAV_ITEM_AI.url)}
          item={APP_NAV_ITEM_AI}
          isOpen
          visibility={visibility}
        />
      )}
      <Accordion.Root type="multiple" value={openAccordionItems} onValueChange={handleOpenAccordionItems}>
        <span className={sectionHeader()}>Apps</span>
        {navItems.map((item, i) => {
          return <OpenItem key={i} isOpen={isOpen} item={item} visibility={visibility} />;
        })}
        <OpenSavedItems
          savedItems={savedItems}
          onDelete={onDeleteSavedItem}
          visibility={visibility}
          isOpen={openAccordionItems?.some((item) => item === 'saved-items')}
        />
      </Accordion.Root>
    </motion.nav>
  );
};

export const openNavStyles = tv({
  slots: {
    base: 'absolute bottom-0 left-2.5 right-2.5 top-5 min-w-36 md:top-0',
    header: 'flex w-full items-center',
    sectionHeader: 'opacity-1 mr-1 pl-2 text-sm uppercase text-lightBlue-600 transition-all',
    icon: 'relative z-2 h-5 w-5 text-lightBlue-500',
    trigger:
      'relative mb-1 flex w-full items-center rounded px-2 py-3 text-baseSm transition-all focus:bg-blue-700 md:mb-0.5 md:p-2 [&>.trigger-icon]:hover:text-white [&[data-state=open]]:bg-blue-700 [&[data-state=open]_.trigger-icon]:rotate-180 [&[data-state=open]_.trigger-icon]:text-white [&_.trigger-bg]:hover:scale-100 [&_.trigger-bg]:hover:opacity-100',
    triggerContent: 'relative z-2 flex w-full items-center',
    triggerIcon: 'trigger-icon h-4 w-4 shrink-0 text-lightBlue-500 transition-all duration-200',
    triggerBg:
      'trigger-bg pointer-events-none absolute bottom-0 left-0 right-0 top-0 z-1 block scale-75 rounded bg-blue-500 opacity-0 transition-all',
    subNav:
      'overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
    subNavList: 'my-1.5 ml-4 -translate-x-px border-l-2 border-blue-500 text-baseSm',
    subNavLink:
      'relative my-1 flex -translate-x-0.5 items-center justify-between gap-2 border-l-2 border-transparent py-0.5 pl-2.5 font-normal transition-colors duration-200 max-md:py-1 max-md:pl-3 [&_span]:hover:h-full [&_span]:hover:bg-lightBlue-500',
    subNavLinkBorder:
      'absolute -left-0.5 top-1/2 block h-2 w-0.5 -translate-y-1/2 bg-transparent duration-150 ease-in-out',
    notificationWrapper: 'absolute -left-1 top-0 z-8',
  },
  variants: {
    isOpen: {
      false: {
        base: 'pointer-events-none',
        sectionHeader: 'opacity-0',
      },
      true: {
        trigger: '!bg-blue-600',
      },
    },
    isActive: {
      true: {
        subNavLinkBorder: 'h-full bg-lightBlue-500',
        subNavLink: 'font-semibold',
        triggerBg: 'scale-100 opacity-100',
      },
    },
  },
});

export { AppNavigationOpen };
